import uniqBy from "lodash/uniqBy";
import { titleCase } from "title-case";
import { getCurrencySymbol } from "common/currency";
import { formatStartingPosition } from "@/utilities/racingUtilities";
import type { Titles } from "@/hooks/graphql/useTitles";
import type { PickemsContestWithMarkets } from "@/hooks/firestore/pickems/usePickemsContest";
import type { PickslipPick } from "@/features/pickems/types/PickslipPick";
import type { PickemsContest } from "@/hooks/firestore/models/pickems";
import type { PickemsContest as PickemsContestOld } from "@/hooks/firestore/pickems/types";
import type { ProcessedPickemSelection } from "@/hooks/firestore/betting/useEntries";

export const getContestLink = (contestId: string, tab = "pick") => {
  return `/pickems/contest/${contestId}/${tab}/`;
};

export const getPlayLink = (contestId: string) => {
  return getContestLink(contestId, "pick");
};

export const getLadderLink = (contestId: string) => {
  return getContestLink(contestId, "results");
};

export const parsePointsEntered = (
  contest: PickemsContestWithMarkets,
  selections: ProcessedPickemSelection[] = [],
  picks: PickslipPick[] = [],
) => {
  let result = 0;

  // in sports contests: we select an outcome from multiple markets,
  // in racing contests: we select a market from multiple events
  const racingContest = isRacingContest(contest);
  const lookupField = racingContest ? "eventId" : "marketId";

  for (const selection of selections) {
    const eventId = selection.event.eventId;
    const marketId = selection.marketId;
    const newPick = picks.find(
      (x) =>
        x[lookupField] === (racingContest ? eventId : marketId) &&
        x.sourceId === contest?.id,
    );
    if (!newPick?.odds) {
      result += Number(selection.points) || 0;
    }
  }
  picks = picks.filter((x) => x.sourceId === contest?.id);
  for (const pick of picks) {
    result += Number(pick.odds) || 0;
  }
  return result;
};

export const getPickDetails = (
  pick: PickslipPick,
  contest: PickemsContestWithMarkets,
) => {
  const market = contest?.markets.find((market) => market.id === pick.marketId);
  const event = market?.event;
  const outcome = market?.outcomes[pick.outcomeId];
  const competitor = outcome?.competitor;
  const marketName = (event?.sport || "") + (event?.eventName || "");

  let eventName;
  let selectionName;

  if (isRacingContest(contest)) {
    // hcpDraw is for harness and we should use that over starting position if its available (PKB-6402)
    const startingPosition = competitor.hcpDraw ?? competitor.startingPosition;
    selectionName = `${competitor.number}. ${competitor.name}${
      event?.sport !== "GREYHOUNDS" && startingPosition
        ? ` (${formatStartingPosition(startingPosition)})`
        : ""
    }`;
    eventName = `${event?.attributes?.venueName} - R${event?.attributes?.raceNumber}`;
  } else {
    selectionName = competitor?.name || titleCase(outcome?.type);
    eventName = event?.eventName;
  }

  const details: PickslipPick = {
    ...pick,
    contest,
    competitor: {
      ...outcome?.competitor,
      silksUrl: market?.event?.attributes.silksUrl,
    },
    eventName,
    freshOdds: outcome?.odds,
    lockedOdds: pick?.odds,
    selectionName,
    marketName,
    accepted: false,
    scheduledStartTime: event?.scheduledStartTime,
    outcome,
  };
  return details;
};

export const getLogos = (sports: string[], titles: Titles) => {
  return (
    sports
      ?.map(
        (x) =>
          titles?.[x.toLowerCase()]?.logo?.file?.url ||
          titles?.[x.toLowerCase()]?.icon?.file?.url,
      )
      .filter(Boolean) || []
  );
};

export const totalPrizePoolAsDouble = (values: number[]) => {
  let result = 0;
  if (values?.length) {
    for (const p of values) {
      result += p;
    }
    result /= 100;
  }
  return result;
};

export const totalPrizePool = (
  values: number[],
  isBonusCash?: boolean,
  currency?: string,
  hasCurrencyFormatting: boolean = true,
) => {
  const value = hasCurrencyFormatting
    ? totalPrizePoolAsDouble(values).toLocaleString()
    : totalPrizePoolAsDouble(values);
  const symbol = getCurrencySymbol(currency);
  const prefix = !isBonusCash ? `${currency ?? ""}${symbol}` : "$";

  return value ? prefix + value : "-";
};

export const getContestOpenRaceMarkets = (
  contest: PickemsContestWithMarkets,
) => {
  return uniqBy(
    contest.markets.filter(
      ({ event, outcomes }) =>
        event.attributes.raceStatus === "OPEN" &&
        Object.keys(outcomes ?? {}).length > 0,
    ),
    ({ event }) => event.id,
  );
};

export const isMarketActiveForPick = (
  contest: PickemsContestWithMarkets,
  pick: PickslipPick | ProcessedPickemSelection,
) => {
  const eventId = "event" in pick ? pick.event.eventId : pick.eventId;
  return contest?.markets.some(
    (market) => market.event?.id === eventId && market?.status === "ACTIVE",
  );
};

export const isRacingContest = (
  contest: PickemsContest | PickemsContestWithMarkets | PickemsContestOld,
) => contest.sportCategories?.includes("RACING");

export const RACE_MARKET_NAMES = [
  { label: "Win", outcomeType: "RUNNER_WIN" },
  { label: "Top 2", outcomeType: "RUNNER_TOP_2" },
  { label: "Top 3", outcomeType: "RUNNER_TOP_3" },
  { label: "Top 4", outcomeType: "RUNNER_TOP_4" },
] as const;

/**
 * Returns a market name for races by outcome type
 */
export const getRaceMarketName = (outcomeType?: string): string => {
  const marketName = RACE_MARKET_NAMES.find(
    (raceMarketName) => raceMarketName.outcomeType === outcomeType,
  );

  return marketName ? marketName.label : "";
};
